import { formatNumber } from '@angular/common';
import { Pipe, PipeTransform, inject } from '@angular/core';

import { Locale } from '@optical/types';
import { UnitSystem, cmToFtIn } from '@optical/utils';

import { UserSettingsStore } from '../state/user-settings';
import { ANGULAR_LOCALE_MAP } from './convert-pipe';

@Pipe({
  name: 'convertHeight',
})
export class ConvertHeightPipe implements PipeTransform {
  private readonly userSettings = inject(UserSettingsStore);

  transform(value: number, includeSuffix = true, digitsInfo = '1.0-1'): string {
    const unitSystem = this.userSettings.unitSystem();
    const locale = this.userSettings.locale();

    return formatHeight(value, unitSystem, locale, includeSuffix, digitsInfo);
  }
}

/**
 * Formats a height value (in centimeters) as a locale-aware string, in either the metric
 * (centimeters) or imperial (feet & inches) unit system.
 *
 * @param value - The height value in centimeters.
 * @param unitSystem - The unit system to format the height in.
 * @param locale - The locale to use for number formatting.
 * @param includeSuffix - Whether to append unit suffixes (`cm`, `ft`, `in`) to the output.
 * @param digitsInfo - The digits info to use for formatting the centimeter value.
 * @returns The formatted height string.
 */
export function formatHeight(
  value: number,
  unitSystem: UnitSystem,
  locale = Locale.EN_US,
  includeSuffix = true,
  digitsInfo = '1.0-1',
): string {
  const angularLocale = ANGULAR_LOCALE_MAP[locale];

  if (unitSystem === UnitSystem.METRIC) {
    const formatted = formatNumber(value, angularLocale, digitsInfo);

    return includeSuffix ? `${formatted} cm` : formatted;
  }

  const { ft, in: inches } = cmToFtIn(value);

  const formattedFeet = formatNumber(ft, angularLocale, '1.0-0');
  const formattedInches = formatNumber(inches, angularLocale, '1.0-0');

  return includeSuffix
    ? `${formattedFeet} ft ${formattedInches} in`
    : `${formattedFeet} ${formattedInches}`;
}
